// ./src/utils/api/TravelTrackingService.ts

import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const TRACKING_POINT_FIELDS = `
  id
  accountId
  travelId
  segmentId
  seq
  latitude
  longiture
  altitude
  speed
  heading
  accuracy
  recordedAt
  createdAt
`;

/**
 * Minimal travel fields returned after tracking mutations.
 * Matches the TRAVEL_FIELDS fragment from TravelService but avoids
 * importing it to keep this service self-contained.
 */
const TRAVEL_TRACKING_RESPONSE_FIELDS = `
  id
  trackingStatus
  currentSegmentId
  gpsDistance
  encodedPolyline
  routeUploadedFileId
  trackingStartedAt
  trackingEndedAt
  status
  version
  updatedAt
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const TRACKING_POINT_LIST_QUERY = gql`
  query TrackingPointList($filter: TrackingPointFilter, $params: PaginationAndSorting) {
    trackingPointList(filter: $filter, params: $params) {
      code
      data {
        ${TRACKING_POINT_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const TRACKING_ADD_POINTS_MUTATION = gql`
  mutation TravelTrackingAddPoints($where: TravelWhereInput, $params: TravelTrackingAddPointsInput) {
    travelTrackingAddPoints(where: $where, params: $params) {
      code
      data {
        ${TRAVEL_TRACKING_RESPONSE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TRACKING_DISCARD_MUTATION = gql`
  mutation TravelTrackingDiscard($where: TravelWhereInput) {
    travelTrackingDiscard(where: $where) {
      code
      data {
        ${TRAVEL_TRACKING_RESPONSE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface TrackingPoint {
  id?: string | undefined;
  accountId?: string | undefined;
  travelId?: string | undefined;
  segmentId?: number | undefined;
  seq?: number | undefined;
  latitude?: number | undefined;
  longitude?: number | undefined;
  altitude?: number | undefined;
  speed?: number | undefined;
  heading?: number | undefined;
  accuracy?: number | undefined;
  recordedAt?: string | undefined;
  createdAt?: string | undefined;
}

export interface TrackingPointInput {
  /** GPS latitude (-90 to 90) */
  latitude: number;
  /** GPS longitude (-180 to 180) */
  longitude: number;
  /** Altitude in meters above sea level */
  altitude?: number | null;
  /** Speed in m/s from GPS */
  speed?: number | null;
  /** Heading in degrees 0-360 from GPS */
  heading?: number | null;
  /** GPS accuracy in meters */
  accuracy?: number | null;
  /** When the GPS reading was taken on the device (ISO 8601) */
  recordedAt: string;
  /** Sequence number, monotonically increasing per travel */
  seq: number;
}

export interface TrackingPointFilter {
  /** Filter by travel ID */
  travelId?: string | null | undefined;
  /** Filter by account ID(s) */
  accountId?: string[] | null | undefined;
  /** Filter by segment ID */
  segmentId?: number | null | undefined;
}

export interface TravelTrackingAddPointsInput {
  /** Array of GPS tracking points */
  points: TrackingPointInput[];
  /** Current GPS-derived total distance in km (running total from client) */
  gpsDistance: number;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * Travel tracking service for GPS point operations.
 * Handles the high-frequency batch upload of tracking points and
 * tracking data management (list, discard).
 *
 * Travel lifecycle operations (start, pause, resume, complete) are handled
 * by TravelService — start via create with enableTracking, pause/resume
 * as dedicated methods, complete via update with lastRecord.
 */
export class TravelTrackingService {
  /**
   * List tracking points for a travel with optional segment filter.
   * Primarily used for debugging or detailed segment analysis.
   */
  static async list(
    filter?: TrackingPointFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(TRACKING_POINT_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Submit a batch of filtered GPS tracking points.
   * Called automatically by the tracking uploader every ~10 seconds.
   * Supports idempotent retries — duplicate seq values are silently skipped.
   *
   * @param travelId - The travel being tracked
   * @param params - Points array and current GPS distance
   */
  static async addPoints(
    travelId: string,
    params: TravelTrackingAddPointsInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      TRACKING_ADD_POINTS_MUTATION,
      {
        where: { id: travelId },
        params,
      }
    );
    return result;
  }

  /**
   * Discard all tracking data for a travel.
   * Hard-deletes all tracking points and resets tracking fields.
   */
  static async discard(travelId: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      TRACKING_DISCARD_MUTATION,
      {
        where: { id: travelId },
      }
    );
    return result;
  }
}